export const VehicleSpines = {
  Vehicles: 'vehicles',
  VehiclePurple: 'vehicle-purple',
  VehicleRed: 'vehicle-red'
} as const;
export type VehicleSpinesKeys = keyof typeof VehicleSpines;
export type VehicleSpinesValues = typeof VehicleSpines[VehicleSpinesKeys];

export const VehicleSkins = {
  Default: 'default',
  CarGreen: 'car-green',
  CarPurple: 'car-purple',
  CarRed: 'car-red',
  TaxiNeutral: 'taxi-neutral',
  TaxiTom: 'taxi-tom',
  TaxiJerry: 'taxi-jerry',
  TaxiSpike: 'taxi-spike',
  TaxiToots: 'taxi-toots'
} as const;
export type VehicleSkinsKeys = keyof typeof VehicleSkins;
export type VehicleSkinsValues = typeof VehicleSkins[VehicleSkinsKeys];

export const TrafficSkinsPool = [VehicleSkins.CarGreen, VehicleSkins.CarPurple, VehicleSkins.CarRed];

export type TaxiSkins =
  | typeof VehicleSkins.TaxiTom
  | typeof VehicleSkins.TaxiJerry
  | typeof VehicleSkins.TaxiSpike
  | typeof VehicleSkins.TaxiToots
  | typeof VehicleSkins.TaxiNeutral;
export const TaxiSkinsPool = [VehicleSkins.TaxiTom, VehicleSkins.TaxiJerry, VehicleSkins.TaxiSpike, VehicleSkins.TaxiToots];

export const VehicleIcons = {
  Tom: 'icon-taxi-tom.png',
  Jerry: 'icon-taxi-jerry.png',
  Spike: 'icon-taxi-spike.png',
  Toots: 'icon-taxi-toots.png'
} as const;
export type VehicleIconsKeys = keyof typeof VehicleIcons;
export type VehicleIconsValues = typeof VehicleIcons[VehicleIconsKeys];

export const CharacterAnimation = {
  Boost: 'boost',
  Bounce: 'bounce',
  Drive: 'drive',
  Impact: 'impact',
  Slide: 'slide',
  Start: 'start',
  Static: 'static'
} as const;

export const CharacterState = {
  Idle: 'idle',
  Driving: 'driving',
  RunSmashing: 'smashing',
  Jumping: 'jumping',
  Falling: 'falling',
  Gliding: 'gliding',
  Dead: 'dead'
} as const;

export type CharacterStateKeys = keyof typeof CharacterState;
export type CharacterStateValues = typeof CharacterState[CharacterStateKeys];

// Types aren't correct - https://github.com/photonstorm/phaser/issues/5463
export default class CharacterSpine extends window.SpinePlugin.SpineGameObject {
  public static skinsToSpine: Map<VehicleSkinsValues, VehicleSpinesValues> = new Map([
    [VehicleSkins.TaxiTom, VehicleSpines.Vehicles],
    [VehicleSkins.TaxiJerry, VehicleSpines.Vehicles],
    [VehicleSkins.TaxiSpike, VehicleSpines.Vehicles],
    [VehicleSkins.TaxiToots, VehicleSpines.Vehicles],
    [VehicleSkins.TaxiNeutral, VehicleSpines.Vehicles],
    [VehicleSkins.CarGreen, VehicleSpines.Vehicles],
    [VehicleSkins.CarPurple, VehicleSpines.VehiclePurple],
    [VehicleSkins.CarRed, VehicleSpines.VehicleRed]
  ]);

  public static events = {
    StopCharacter: 'stopCharacter'
  } as const;

  protected skins: VehicleSkinsValues[];
  protected currentSkinIndex: number;
  protected isMoving: boolean;
  protected currentState: CharacterStateValues;

  constructor(scene: Phaser.Scene, x: number, y: number, skin: VehicleSkinsValues) {
    super(scene, scene.sys.spine, x, y, CharacterSpine.skinsToSpine.get(skin), CharacterAnimation.Static);

    this.skins = Object.values(VehicleSkins);
    this.currentSkinIndex = this.skins.indexOf(skin);
    this.setSkinByName(skin);
    this.scale = 0.5;

    this.isMoving = false;
    this.currentState = CharacterState.Idle;

    // this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.on(Phaser.GameObjects.Events.DESTROY, () => {
      // this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
      // this.destroy();
    });

    this.preMultipliedAlpha = true;
    // this.scene.events.once(CharacterSpine.events.StopCharacter, this.stopMoving, this);
  }

  get CurrentSkin() {
    return this.skins[this.currentSkinIndex % this.skins.length];
  }

  public switchSkin(skin: VehicleSkinsValues) {
    if (this.findSkin(skin)) {
      this.setSkinByName(skin);
      this.currentSkinIndex = this.skins.indexOf(skin);
    } else {
      // @ts-ignore - Phaser doc is wrong on parameters.
      this.setSkeleton(CharacterSpine.skinsToSpine.get(skin));
      this.setSkinByName(skin);
      this.currentSkinIndex = this.skins.indexOf(skin);
    }
  }

  public cycleCharacter() {
    this.currentSkinIndex++;
    this.setSkinByName(this.skins[this.currentSkinIndex % this.skins.length]);
  }

  public startMoving() {
    this.isMoving = true;
    this.drive(false);
    // this.setAnimation(0, CharacterAnimation.Start, false);
    // this.once('end', () => {
    //   this.drive();
    // });
  }

  public stopMoving() {
    this.isMoving = false;
    this.setAnimation(0, CharacterAnimation.Impact, false);
    this.once('end', () => {
      this.addAnimation(0, CharacterAnimation.Static, true);
    });
  }

  // #region animations

  public drive(waitForEnd: boolean) {
    this.currentState = CharacterState.Driving;
    if (waitForEnd) this.addAnimation(0, CharacterAnimation.Drive, true);
    else this.setAnimation(0, CharacterAnimation.Drive, true);
  }

  public boost() {
    this.setAnimation(0, CharacterAnimation.Boost, false);
    this.once('end', () => {
      if (this.isMoving) this.drive(true);
    });
  }

  public bounce() {
    this.setAnimation(0, CharacterAnimation.Bounce, false);
    this.once('end', () => {
      if (this.isMoving) this.drive(true);
    });
  }

  public slide() {
    this.setAnimation(0, CharacterAnimation.Slide, false);
    this.once('end', () => {
      if (this.isMoving) this.drive(true);
    });
  }

  public impact() {
    this.setAnimation(0, CharacterAnimation.Impact, false);
    this.once('end', () => {
      if (this.isMoving) this.drive(true);
    });
  }

  public die() {
    this.currentState = CharacterState.Dead;
    // this.setAnimation(0, CharacterAnimation.Death, false);
  }

  // #endregion
}
